<template>
  <RfSimpleCard class="p-10">
    <div
      v-if="app.web_snippet_settings?.userid_store_type"
      class="bg-goal mb-5 flex flex-row gap-3 rounded px-5 py-3"
    >
      <RfStatusIcon class="fill-shade-3 mt-0.5 h-6 w-6" />
      <div>
        Your
        <RouterLink class="underline!" :to="{ path: `/apps/${app.id}/settings/user_id` }"
          >User ID Matching settings</RouterLink
        >
        are currently overriding the <code>fetchUserId</code> function below.
        <br />
        To use the below, select "Fetch from: Custom JS Snippet" instead.
      </div>
    </div>
    <div class="rf-custom-web-card-wrapper">
      <component :is="codeEditor.CodeEditor" v-model="code" v-bind="codeEditor.props"></component>
    </div>
  </RfSimpleCard>
</template>

<script>
import RfSimpleCard from "@/components/RfSimpleCard.vue";
import RfStatusIcon from "../icons/RfStatusIcon.vue";
import { RouterLink } from "vue-router";
import { useCodeEditor } from "@/utils/composables/useCodeEditor";
import validateUserJs from "@/utils/ValidateUserJs";

export default {
  name: "RfCustomWebSnippet",
  components: { RfSimpleCard, RfStatusIcon, RouterLink },
  props: ["app"],
  setup: () => ({ codeEditor: useCodeEditor() }),
  data() {
    return {
      success: false,
      code: this.getSnippet(),
    };
  },
  methods: {
    fillModel(model) {
      const validateJs = validateUserJs(this.code);
      if (!validateJs.ok) {
        throw new Error(`Web snippet has syntax errors: ${validateJs.error.message}`);
      }
      model.web_snippet = this.code;
      const success = model.web_snippet && !!model.web_snippet.length;
      if (!success) {
        throw new Error("Web snippet requires a value");
      }
    },
    getSnippet() {
      if (this.app && this.app.web_snippet && `${this.app.web_snippet}`.trim().length > 0) {
        return this.app.web_snippet;
      }
      return `export default class Settings {
  static async fetchUserId() {
    /*
    REQUIRED
    Return the authenticated user's unique ID.
    This string can come from cookies, localstorage, db, or an async endpoint fetch.
    EXAMPLE BELOW
    */
    /*
    const user = localStorage.getItem("user_object");
    return user.id
    */

    return null;
  }

  static async fetchUserJwt() {
    /*
    You can return a signed JWT containing { user_id, exp } to improve security.
    Sign the JWT using the secret provided in User ID Matching settings.
    This will override fetchUserId if present.
    */

    return null;
  }

  static async fetchAnonUserId() {
    /*
    Return a stable anonymous ID for unauthenticated users.
    If omitted, a value will be automatically generated.
    */

    return null;
  }

  static async fetchUserTraits() {
    /*
    You can fetch data from your frontend to send to us.
    Data can come from cookies, localstorage, db, or an async endpoint fetch.
    EXAMPLE BELOW
    */
    /*
    const user = localStorage.getItem("user_object");
    return {
      is_registered: !!user.token,
      member_since: !!user.registrationDate,
      accepted_tos: localStorage.getItem("tos_acceptance_date")
    }
    */

    return null;
  }

  static canShowPromotion() {
    /*
    You can specify any app-wide conditions under which prompts should never be shown
    Should return a Boolean. If value is false, no overlay prompts will be shown
    EXAMPLE BELOW
    */
    /*
    return !!document.querySelector('.full-screen-video-player')
    */

    return true;
  }

  static onPromptInteraction(eventName, payload) {
    /*
    This function is called whenever a prompt event occurs, for custom analytics purposes
    @param {string} eventName: impression, click, click2, decline, dismiss, timeout, holdout
    @param {object} payload: {
      activity: "Redfast Prompt Click",
      cta: "CTA Button Text",
      el: <HTMLElement>,
      event_timestamp: "2025-01-01T08:00:00.000Z",
      promo_id: "abcd1234-1234-abcd-1234-abcdef123456",
      promo_name: "Prompt Name",
      user_id: "abcdef1234567890abcdef1234567890abcdef1234567890abcdef1234567890",
      variation_id: "abcd1234-1234-abcd-1234-abcdef123456",
      variation_name: "Variation Name",
      redirect_url: "http://example.com/website_action_redirect_url",
      rf_metadata: { key: "value" },
      experiment_name: "Experiment Name",
      experiment_id: "abcd1234-1234-abcd-1234-abcdef123456",
      promo_input_1_value: "form input value",
      promo_input_2_value: "form input value",
      promo_input_3_value: "form input value",
      survey_input_value: "survey input value"
    }
    */
  }

  static canPing() {
    /*
    You can specify whether the user has opted into being identified by Recurly Engage.
    If the value is false, user information will not be collected.
    Can be used if user has opted out of CCPA-GDPR.
    */

    return true;
  }

  static setLanguage() {
    /*
    You can specify the language of the user. Will control the display language of prompts
    that have languages configured. Should return a 2-letter or 4-letter language code.
    */

    return null
  }
}`;
    },
  },
};
</script>
